import type { GetRequestsListItem } from "@nlc/shared-types";
import type { SortDescriptor } from "react-stately";

import { UserRoleEnum } from "@nlc/shared-types";

import type { DragDropHandlers } from "@/features/requests/types/tables";

import { ConfigurableTable } from "@/features/requests/components/tables";
import { useReorderQueueRequests } from "@/features/requests/hooks/shared/useReorderQueueRequests";
import { useUser } from "@/hooks/useUser";

import type { DropdownFilterType } from "../types";

import {
  projectDraftsRequestsTableConfig,
  projectQueuedRequestsTableConfig,
  projectRequestsDesignerTableConfig,
  projectRequestsTableConfig,
} from "../config/tableConfig";

interface ProjectRequestsTableProps {
  data: GetRequestsListItem[];
  dropdownFilter: DropdownFilterType;
  isDesigner?: boolean;
  isLoading: boolean;
  itemsPerPage: number;
  onClearSelection?: () => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  onPageChange: (page: number) => void;
  onSelectAll?: () => void;
  onSelectionChange?: (selectedItems: Set<number | string>) => void;
  onSortChange: (sortDescriptor: SortDescriptor) => void;
  page: number;
  selectedItems?: Set<number | string>;
  sortDescriptor: SortDescriptor;
  totalItems: null | number;
  totalPages: null | number;
}

const createDragDropHandlers = (
  reorderMutation: ReturnType<typeof useReorderQueueRequests>,
): DragDropHandlers<GetRequestsListItem> => ({
  onReorder: () => {
    // For immediate UI feedback, table component handles visual reordering
  },
  onSave: async (reorderedData: GetRequestsListItem[]) => {
    const orderedRequestIds = reorderedData.map((request) => request.id);

    await reorderMutation.mutateAsync(orderedRequestIds);
  },
});

export function ProjectRequestsTable({
  data,
  dropdownFilter,
  isDesigner = false,
  isLoading,
  itemsPerPage,
  onClearSelection,
  onItemsPerPageChange,
  onPageChange,
  onSelectAll,
  onSelectionChange,
  onSortChange,
  page,
  selectedItems,
  sortDescriptor,
  totalItems,
  totalPages,
}: ProjectRequestsTableProps) {
  const { data: userData } = useUser();

  const reorderMutation = useReorderQueueRequests();

  const canReorderQueue =
    userData?.user?.roleId === UserRoleEnum.SuperAdmin ||
    userData?.user?.roleId === UserRoleEnum.TeamLeader ||
    userData?.user?.roleId === UserRoleEnum.CustomerSuccess;

  const isQueuedFilter = dropdownFilter === "queued";
  const isDraftsFilter = dropdownFilter === "drafts";
  const config = isQueuedFilter
    ? projectQueuedRequestsTableConfig
    : isDraftsFilter
      ? projectDraftsRequestsTableConfig
      : isDesigner
        ? projectRequestsDesignerTableConfig
        : projectRequestsTableConfig;

  const selectionEnabled =
    !isQueuedFilter && onSelectionChange && selectedItems;

  return (
    <ConfigurableTable
      config={config}
      data={data}
      dragDropHandlers={
        isQueuedFilter && canReorderQueue
          ? createDragDropHandlers(reorderMutation)
          : undefined
      }
      isLoading={isLoading}
      paginationProps={
        isQueuedFilter
          ? undefined
          : {
              metadata: {
                currentPage: page,
                itemsPerPage,
                totalItems,
                totalPages,
              },
              onItemsPerPageChange,
              onPageChange,
              pageSizeOptions: [10, 25, 50, 100],
              showPageSizeSelector: true,
            }
      }
      selectionConfig={
        selectionEnabled
          ? {
              enabled: true,
              key: config.id,
              onClearSelection,
              onSelectAll,
              onSelectionChange,
              selectedItems,
            }
          : undefined
      }
      setSortDescriptor={onSortChange}
      sortDescriptor={sortDescriptor}
      useAdvancedSorting={true}
    />
  );
}
